import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';

import bcrypt from 'bcryptjs';

import {
  Navigate,
  useNavigate,
} from 'react-router-dom';

import AppLayout from '../../components/Layout/AppLayout';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';
import InfoTooltip from '../../components/UI/InfoTooltip';
import AppDownloadCard from '../../components/Downloads/AppDownloadCard';
import { copyTextToClipboard } from '../../utils/clipboard';
import '../../styles/brand-typography.css';
import './AddChild.css';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';

import {
  useAuth,
} from '../../contexts/AuthContext';

import {
  db,
} from '../../services/supabase';

import {
  AVATARS,
} from '../../utils/constants';

import {
  AGE_GROUP_OPTIONS,
} from '../../utils/ageGroups';
import {
  detectFamilyTimeZone,
  getFamilyTimeZoneOptions,
} from '../../utils/timeZones';


const SUFFIX_OPTIONS = [
  '',
  'Jr.',
  'Sr.',
  'II',
  'III',
  'IV',
  'V',
];


const EMPTY_CHILD_FORM = {
  first_name: '',
  middle_initial: '',
  last_name: '',
  suffix: '',
  nickname: '',
  age_group: '',
  avatar: AVATARS[0],
  avatar_url: '',
  pin: '',
  confirm_pin: '',
  require_chore_approval: true,
};


const SCREENS = {
  WELCOME: 'welcome',
  PRIVACY: 'privacy',
  FAMILY_NAME: 'family_name',
  FIRST_CHILD: 'first_child',
  CHILD_CREATED: 'child_created',
};


const getInitialScreen = (parent) => {
  if (
    parent?.onboarding_step ===
      'first_child'
  ) {
    return SCREENS.FIRST_CHILD;
  }

  if (
    parent?.onboarding_step ===
      'family_name'
  ) {
    return SCREENS.FAMILY_NAME;
  }

  return SCREENS.WELCOME;
};


const SetupProgress = ({
  currentStep,
}) => (
  <div
    className="mb-4"
    aria-label={`Getting started step ${currentStep} of 4`}
  >
    <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
      <span>Getting Started</span>
      <span>
        Step {currentStep} of 4
      </span>
    </div>

    <div className="mt-2 grid grid-cols-4 gap-2">
      {[1, 2, 3, 4].map(
        (step) => (
          <div
            key={step}
            className={`h-2 rounded-full ${
              step <= currentStep
                ? 'bg-blue-600 dark:bg-blue-500'
                : 'bg-gray-200 dark:bg-slate-700'
            }`}
            aria-hidden="true"
          />
        )
      )}
    </div>
  </div>
);


export default function ParentOnboarding() {
  const navigate =
    useNavigate();

  const {
    parent,
    refreshParent,
  } = useAuth();

  const existingFamilyName =
    parent
      ?.families_1740480000000
      ?.display_name || '';

  const existingTimeZone =
    parent
      ?.families_1740480000000
      ?.time_zone ||
    detectFamilyTimeZone();

  const [
    screen,
    setScreen,
  ] = useState(
    getInitialScreen(parent)
  );

  const [
    privacyAcknowledged,
    setPrivacyAcknowledged,
  ] = useState(false);

  const [
    familyName,
    setFamilyName,
  ] = useState(existingFamilyName);

  const [
    familyTimeZone,
    setFamilyTimeZone,
  ] = useState(existingTimeZone);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState('');

  const [
    childForm,
    setChildForm,
  ] = useState(
    EMPTY_CHILD_FORM
  );

  const [
    createdChild,
    setCreatedChild,
  ] = useState(null);

  const [
    copied,
    setCopied,
  ] = useState(false);

  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const pageContent = useRef(null);
  const creatingChild = useRef(false);
  useEffect(() => {
    window.scrollTo({ top: 0 });
    pageContent.current?.querySelector('h2')?.focus({ preventScroll: true });
  }, [screen]);

  const parentFirstName =
    useMemo(() => {
      const name =
        parent?.name?.trim();

      return name
        ? name.split(/\s+/)[0]
        : 'there';
    }, [parent?.name]);

  const familyPreview =
    familyName.trim()
      ? `${familyName.trim()} Family`
      : 'Your Family';


  if (parent?.role !== 'owner') {
    return (
      <Navigate
        to="/parent"
        replace
      />
    );
  }


  const saveProgress = async (step) => {
    await db.updateMyParentOnboarding(
      'in_progress',
      step
    );

    await refreshParent();
  };


  const handlePrivacyContinue =
    async () => {
      if (
        !privacyAcknowledged ||
        saving
      ) {
        return;
      }

      setSaving(true);
      setError('');

      try {
        await saveProgress(
          'family_name'
        );

        setScreen(
          SCREENS.FAMILY_NAME
        );
      } catch (progressError) {
        console.error(
          'Onboarding progress error:',
          progressError
        );

        setError(
          progressError?.message ||
            'Unable to continue right now. Please try again.'
        );
      } finally {
        setSaving(false);
      }
    };


  const continueToChildStep =
    async () => {
      await saveProgress(
        'first_child'
      );

      setScreen(
        SCREENS.FIRST_CHILD
      );
    };


  const handleChildChange =
    (event) => {
      const {
        name,
        value,
      } = event.target;

      setChildForm(
        (current) => ({
          ...current,
          [name]: value,
        })
      );
    };


  const startDashboardTour =
    async () => {
      if (saving) {
        return;
      }

      setSaving(true);
      setError('');

      try {
        await db.updateMyParentOnboarding(
          'in_progress',
          'manage_family'
        );

        await refreshParent();

        navigate('/parent', {
          replace: true,
          state: {
            dashboardTour: 'initial',
          },
        });
      } catch (tourError) {
        console.error(
          'Start onboarding tour error:',
          tourError
        );

        setError(
          tourError?.message ||
            'Unable to start the dashboard tour. Please try again.'
        );
      } finally {
        setSaving(false);
      }
    };


  const handleAddChild =
    async (event) => {
      event.preventDefault();

      if (saving || creatingChild.current) {
        return;
      }

      setError('');

      const firstName =
        childForm.first_name.trim();

      const lastName =
        childForm.last_name.trim();

      const middleInitial =
        childForm.middle_initial
          .trim()
          .toUpperCase();

      const nickname =
        childForm.nickname.trim();

      if (!firstName) {
        setError(
          'First name is required.'
        );
        return;
      }

      if (!lastName) {
        setError(
          'Last name is required.'
        );
        return;
      }

      if (
        middleInitial &&
        !/^[A-Z]$/.test(
          middleInitial
        )
      ) {
        setError(
          'Middle initial must be a single letter.'
        );
        return;
      }

      if (!nickname) {
        setError(
          'Nickname / display name is required.'
        );
        return;
      }

      if (
        !AGE_GROUP_OPTIONS.some(
          (option) =>
            option.value ===
            childForm.age_group
        )
      ) {
        setError(
          'Please select an age group.'
        );
        return;
      }

      if (
        childForm.pin.length < 6 || childForm.pin.length > 64
      ) {
        setError(
          'Password must be 6 to 64 characters.'
        );
        return;
      }

      if (
        childForm.pin !==
        childForm.confirm_pin
      ) {
        setError(
          'Passwords do not match.'
        );
        return;
      }

      if (childForm.avatar_url.trim()) {
        try {
          const picture = new URL(childForm.avatar_url.trim());
          if (!['https:', 'http:'].includes(picture.protocol)) {
            throw new Error('Invalid protocol');
          }
        } catch {
          setError('Picture URL must be a valid http:// or https:// link.');
          return;
        }
      }

      creatingChild.current = true;
      setSaving(true);

      try {
        const pinHash =
          await bcrypt.hash(
            childForm.pin,
            10
          );

        const child =
          await db.createChild({
            family_id:
              parent.family_id,
            first_name: firstName,
            middle_initial:
              middleInitial || null,
            last_name: lastName,
            suffix:
              childForm.suffix ||
              null,
            nickname,
            age_group:
              childForm.age_group,
            avatar:
              childForm.avatar,
            avatar_url:
              childForm.avatar_url
                .trim() || null,
            pin_hash: pinHash,
            require_chore_approval:
              childForm
                .require_chore_approval,
            status: 'active',
          });

        try {
          await db.updateMyParentOnboarding(
            'in_progress',
            'manage_family'
          );
        } catch (progressError) {
          // The child was created successfully. Keep showing the
          // generated Login ID and retry this transition when the
          // parent starts the tour, rather than inviting a duplicate.
          console.error(
            'Child created; tour progress will retry:',
            progressError
          );
        }

        setChildForm(
          (current) => ({
            ...current,
            pin: '',
            confirm_pin: '',
          })
        );

        setCreatedChild(child);
        setScreen(
          SCREENS.CHILD_CREATED
        );
      } catch (childError) {
        console.error(
          'Onboarding child creation error:',
          childError
        );

        setError(
          childError?.message ||
            'Unable to add this child. Please try again.'
        );
      } finally {
        creatingChild.current = false;
        setSaving(false);
      }
    };


  const handleCopyLoginId =
    async () => {
      const loginId =
        createdChild?.user_id;

      if (!loginId) {
        return;
      }

      try {
        if (!await copyTextToClipboard(loginId)) throw new Error('Clipboard unavailable');

        setCopied(true);

        window.setTimeout(
          () => setCopied(false),
          2000
        );
      } catch (copyError) {
        console.error(
          'Copy Login ID error:',
          copyError
        );

        setError(
          'Unable to copy automatically. Press and hold the Login ID to copy it.'
        );
      }
    };


  const handleSaveFamilyName =
    async (event) => {
      event.preventDefault();

      if (saving) {
        return;
      }

      const nextName =
        familyName.trim();

      setError('');

      if (!nextName) {
        setError(
          'Enter a family name or choose Set Up Later.'
        );
        return;
      }

      if (nextName.length > 20) {
        setError(
          'Family name must be 20 characters or fewer.'
        );
        return;
      }

      if (
        /^family$/i.test(nextName) ||
        /\s+family$/i.test(nextName)
      ) {
        setError(
          'Enter the family name without the word “Family.”'
        );
        return;
      }

      setSaving(true);

      try {
        const savedName =
          await db.updateMyFamilyDisplayName(
            nextName
          );

        setFamilyName(
          savedName || nextName
        );

        await db.updateMyFamilyTimeZone(
          familyTimeZone
        );

        await continueToChildStep();
      } catch (saveError) {
        console.error(
          'Onboarding family-name error:',
          saveError
        );

        setError(
          saveError?.message ||
            'Unable to save the family name. Please try again.'
        );
      } finally {
        setSaving(false);
      }
    };


  const handleSkipFamilyName =
    async () => {
      if (saving) {
        return;
      }

      setSaving(true);
      setError('');

      try {
        await db.updateMyFamilyTimeZone(
          familyTimeZone
        );

        await continueToChildStep();
      } catch (skipError) {
        console.error(
          'Skip family-name error:',
          skipError
        );

        setError(
          skipError?.message ||
            'Unable to continue right now. Please try again.'
        );
      } finally {
        setSaving(false);
      }
    };


  return (
    <AppLayout
      title="Welcome to MyPiggy"
      className="mypiggy-brand add-child"
      showSettings={false}
    >
      <div ref={pageContent} className="max-w-2xl mx-auto">

        {screen === SCREENS.WELCOME && (
          <Card className="p-4 sm:p-5">
            <SetupProgress
              currentStep={1}
            />

            <div className="text-center">
              <MyPiggyIcon name="my-piggy-bank" size="lg" />

              <h2 tabIndex={-1} className="mt-4 text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-slate-100">
                Help Your Child Build Confident Money Habits
              </h2>

              <p className="mt-3 text-gray-600 dark:text-slate-300 leading-relaxed">
                Welcome, {parentFirstName}! MyPiggy helps children keep track of the real money in their physical piggy bank while learning responsible saving and spending habits.
              </p>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-4">
                <MyPiggyIcon name="money-received" size="md" />
                <h3 className="mt-2 font-bold text-gray-900 dark:text-slate-100">
                  Practice Real Money Skills
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-slate-300">
                  Record money received, purchases, savings, goals, and chores through hands-on practice.
                </p>
              </div>

              <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 p-4">
                <MyPiggyIcon name="learning-tip" size="md" />
                <h3 className="mt-2 font-bold text-gray-900 dark:text-slate-100">
                  Build Lifelong Habits
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-slate-300">
                  Explore foundational money-management and accounting concepts together as a family.
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-4 py-3">
              <p className="text-sm text-amber-900 dark:text-amber-200">
                MyPiggy is an educational tracking tool. It does not hold, move, or manage real money.
              </p>
            </div>

            <Button
              type="button"
              className="mt-4 w-full"
              size="lg"
              onClick={() => {
                setError('');
                setScreen(
                  SCREENS.PRIVACY
                );
              }}
            >
              Let’s Get Started
            </Button>
          </Card>
        )}


        {screen === SCREENS.PRIVACY && (
          <Card className="p-4 sm:p-5">
            <SetupProgress
              currentStep={2}
            />

            <div className="text-center">
              <MyPiggyIcon name="parent-approval" size="lg" />

              <h2 tabIndex={-1} className="mt-4 text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-slate-100">
                Your Family’s Privacy Matters
              </h2>
            </div>

            <div className="mt-4 space-y-4 text-sm leading-relaxed text-gray-700 dark:text-slate-300">
              <p>
                MyPiggy is an educational money-tracking application. It is not a bank, financial institution, investment service, or financial adviser.
              </p>

              <p>
                MyPiggy does not connect to bank accounts, credit cards, payment services, or other financial institutions. It cannot transfer or withdraw money.
              </p>

              <p>
                We collect only the information needed to create and operate your MyPiggy family account, including the parent account information and family or child details you choose to provide.
              </p>

              <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-4 py-3 text-red-800 dark:text-red-200">
                Do not enter bank account numbers, card numbers, Social Security numbers, or other sensitive financial information into MyPiggy.
              </div>
            </div>

            <label className="mt-4 flex items-start gap-3 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50 p-4 cursor-pointer">
              <input
                type="checkbox"
                checked={
                  privacyAcknowledged
                }
                onChange={(event) =>
                  setPrivacyAcknowledged(
                    event.target.checked
                  )
                }
                disabled={saving}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />

              <span className="text-sm text-gray-700 dark:text-slate-300">
                I understand that MyPiggy is an educational tracking tool and does not hold, transfer, or connect to real money.
              </span>
            </label>

            {error && (
              <div className="mt-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-4 py-3 text-sm text-red-700 dark:text-red-300" role="alert">
                {error}
              </div>
            )}

            <div className="mt-4 flex flex-col-reverse sm:flex-row gap-3 sm:justify-between">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setError('');
                  setScreen(
                    SCREENS.WELCOME
                  );
                }}
                disabled={saving}
              >
                Back
              </Button>

              <Button
                type="button"
                onClick={
                  handlePrivacyContinue
                }
                disabled={
                  !privacyAcknowledged ||
                  saving
                }
              >
                {saving
                  ? 'Continuing...'
                  : 'Continue'}
              </Button>
            </div>
          </Card>
        )}


        {screen === SCREENS.FAMILY_NAME && (
          <Card className="p-4 sm:p-5">
            <SetupProgress
              currentStep={3}
            />

            <div className="text-center">
              <div className="text-5xl" aria-hidden="true">
                👪
              </div>

              <h2 tabIndex={-1} className="mt-4 text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-slate-100">
                Name Your Family
              </h2>

              <p className="mt-3 text-gray-600 dark:text-slate-300">
                Add a short family name to personalize your MyPiggy experience, or set it up later.
              </p>
            </div>

            <form
              className="mt-4 space-y-5"
              onSubmit={
                handleSaveFamilyName
              }
            >
              <Input
                label="Family Name"
                value={familyName}
                onChange={(event) =>
                  setFamilyName(
                    event.target.value
                  )
                }
                maxLength={20}
                autoComplete="off"
                placeholder="For example, Parikh"
                helperText="Enter the family name only. We’ll add “Family” automatically."
                disabled={saving}
                autoFocus
              />

              <div>
                <label
                  htmlFor="family-time-zone"
                  className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2"
                >
                  Family Time Zone
                </label>
                <select
                  id="family-time-zone"
                  value={familyTimeZone}
                  onChange={(event) =>
                    setFamilyTimeZone(event.target.value)
                  }
                  disabled={saving}
                  className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-gray-900 dark:text-slate-100 disabled:opacity-60"
                >
                  {getFamilyTimeZoneOptions(familyTimeZone).map(
                    (option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    )
                  )}
                </select>
                <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">
                  We detected this automatically. It determines when daily chores become available again.
                </p>
              </div>

              <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 px-4 py-4 text-center">
                <div className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                  Preview
                </div>
                <div className="mt-1 text-xl font-bold text-blue-950 dark:text-blue-100 break-words">
                  {familyPreview}
                </div>
              </div>

              <p className="text-sm text-gray-600 dark:text-slate-400">
                This name is used only for personalization. Your Family Code and kid Login IDs are separate and will never change because of it.
              </p>

              {error && (
                <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-4 py-3 text-sm text-red-700 dark:text-red-300" role="alert">
                  {error}
                </div>
              )}

              <div className="flex flex-col gap-3">
                <Button
                  type="submit"
                  size="lg"
                  disabled={saving}
                >
                  {saving
                    ? 'Saving...'
                    : 'Save Family Name'}
                </Button>

                <button
                  type="button"
                  onClick={
                    handleSkipFamilyName
                  }
                  disabled={saving}
                  className="text-sm font-semibold text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-50"
                >
                  Set Up Later
                </button>
              </div>
            </form>
          </Card>
        )}


        {screen === SCREENS.FIRST_CHILD && (
          <Card className="p-4 sm:p-5">
            <SetupProgress
              currentStep={4}
            />

            <div className="text-center">
              <div className="text-5xl" aria-hidden="true">
                🧒
              </div>

              <h2 tabIndex={-1} className="mt-4 text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-slate-100">
                Add Your First Child
              </h2>

              <p className="mt-3 text-gray-600 dark:text-slate-300 leading-relaxed">
                Create a child profile now, or explore MyPiggy first and set it up later.
              </p>
            </div>

            <div className="mt-5 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-200">
              We use a broad age group instead of an exact age. Your child’s permanent Login ID will be generated automatically. Profile details, goals, passwords, and chore approval are available later under Manage Child.
            </div>

            <form
              className="mt-4 space-y-4"
              onSubmit={handleAddChild}
            >
              <fieldset
                disabled={saving}
                className="space-y-4"
              >
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="First Name" name="first_name" value={childForm.first_name} onChange={handleChildChange} placeholder="e.g. Jia" autoComplete="off" required />
                <Input label="Last Name" name="last_name" value={childForm.last_name} onChange={handleChildChange} placeholder="e.g. Parikh" autoComplete="off" required />
              </div>
              <details className="rounded-lg border border-gray-200 px-3 py-2 dark:border-slate-700"><summary className="cursor-pointer text-xs font-bold text-teal-800 dark:text-teal-200">Additional name details (optional){childForm.middle_initial || childForm.suffix ? ' • Added' : ''}</summary><div className="mt-3 grid grid-cols-2 gap-3"><Input label="Middle Initial" name="middle_initial" value={childForm.middle_initial} onChange={(e) => setChildForm((previous) => ({ ...previous, middle_initial: e.target.value.replace(/[^A-Za-z]/g, '').slice(0, 1).toUpperCase() }))} maxLength={1} autoComplete="off" placeholder="M" /><div><label htmlFor="suffix">Suffix</label><select id="suffix" name="suffix" value={childForm.suffix} onChange={handleChildChange}>{SUFFIX_OPTIONS.map((suffix) => <option key={suffix || 'none'} value={suffix}>{suffix || 'None'}</option>)}</select></div></div></details>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Nickname / Display Name" name="nickname" value={childForm.nickname} onChange={handleChildChange} placeholder="What should MyPiggy call them?" helperText="Shown on the kid dashboard; their Login ID stays the same." required />
                <div><div className="mb-1.5 flex items-center gap-2"><label htmlFor="age_group" style={{ marginBottom: 0 }}>Age Group</label><InfoTooltip compact label="About child age groups">We store a broad age group rather than an exact age. Choose the best fit; there is no upper age limit for 18+.</InfoTooltip></div><select id="age_group" name="age_group" value={childForm.age_group} onChange={handleChildChange} required><option value="">Select an age group</option>{AGE_GROUP_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></div>
              </div>
              <div className="rounded-xl border border-teal-200 bg-teal-50 p-3 dark:border-teal-800 dark:bg-teal-950/30"><div className="flex items-start gap-2"><input id="require-chore-approval" type="checkbox" checked={childForm.require_chore_approval} onChange={(e) => setChildForm((previous) => ({ ...previous, require_chore_approval: e.target.checked }))} className="mt-1 h-4 w-4 shrink-0 accent-teal-700" /><div className="min-w-0 flex-1"><label htmlFor="require-chore-approval">Require Parent Approval for Chores</label><p className="text-xs text-gray-600 dark:text-slate-300">{childForm.require_chore_approval ? 'Review completed chores before rewards are added.' : 'Completed chores are approved and rewarded automatically.'}</p></div><InfoTooltip compact label="About chore approval">This setting applies to all chores completed by this child. When enabled, rewards wait for parent approval. When disabled, completed chores are approved automatically. You can change this later under Manage Child.</InfoTooltip></div></div>
              <div className="rounded-xl border border-gray-200 p-3 dark:border-slate-700">
                <div className="flex items-center gap-3"><span className="text-3xl" aria-hidden="true">{childForm.avatar}</span><div className="flex-1"><p className="text-sm font-bold">Avatar</p><p className="text-xs text-gray-600 dark:text-slate-300">Choose a look for their profile.</p></div><Button type="button" size="sm" variant="secondary" aria-expanded={showAvatarPicker} aria-controls="child-avatar-picker" onClick={() => setShowAvatarPicker((value) => !value)}>{showAvatarPicker ? 'Done' : 'Change Avatar'}</Button></div>
                {showAvatarPicker && <div id="child-avatar-picker" className="mt-3 grid grid-cols-5 gap-2">{AVATARS.map((avatar) => <button key={avatar} type="button" aria-label={`Choose ${avatar} avatar`} aria-pressed={childForm.avatar === avatar} onClick={() => setChildForm((previous) => ({ ...previous, avatar }))} className={`min-h-11 rounded-lg border-2 p-2 text-2xl transition-colors focus-visible:ring-2 focus-visible:ring-teal-500 ${childForm.avatar === avatar ? 'border-teal-600 bg-teal-50 dark:border-teal-400 dark:bg-teal-950/50' : 'border-gray-200 hover:border-teal-400 dark:border-slate-600'}`}>{avatar}</button>)}</div>}
                <details className="mt-2"><summary className="cursor-pointer text-xs font-bold text-teal-800 dark:text-teal-200">Use a picture link (optional){childForm.avatar_url ? ' • Added' : ''}</summary><div className="mt-2"><Input label="Picture URL" name="avatar_url" value={childForm.avatar_url} onChange={handleChildChange} placeholder="https://…" /><p className="mt-1 text-xs text-gray-600 dark:text-slate-300">A valid http:// or https:// picture link replaces the emoji on their profile.</p></div></details>
              </div>
              <div className="grid gap-3 sm:grid-cols-2"><Input label="Create a Password" type="password" name="pin" value={childForm.pin} onChange={(e) => setChildForm((previous) => ({ ...previous, pin: e.target.value.slice(0, 64) }))} placeholder="6 to 64 characters" autoComplete="new-password" maxLength={64} required /><Input label="Confirm Password" type="password" name="confirm_pin" value={childForm.confirm_pin} onChange={(e) => setChildForm((previous) => ({ ...previous, confirm_pin: e.target.value.slice(0, 64) }))} placeholder="Re-enter the password" autoComplete="new-password" maxLength={64} required /></div>
                {error && (
                  <div
                    className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-4 py-3 text-sm text-red-700 dark:text-red-300"
                    role="alert"
                  >
                    {error}
                  </div>
                )}

                <div className="flex flex-col gap-3">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={saving}
                  >
                    {saving
                      ? 'Adding Child...'
                      : 'Add Child'}
                  </Button>

                  <button
                    type="button"
                    onClick={startDashboardTour}
                    disabled={saving}
                    className="text-sm font-semibold text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-50"
                  >
                    Set Up Later
                  </button>
                </div>
              </fieldset>
            </form>
          </Card>
        )}


        {screen === SCREENS.CHILD_CREATED && createdChild && (
          <Card className="p-4 sm:p-5 text-center">
            <div className="text-5xl" aria-hidden="true">
              {createdChild.avatar || '🎉'}
            </div>

            <h2 tabIndex={-1} className="mt-4 text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-slate-100">
              Child Added Successfully!
            </h2>

            <p className="mt-2 text-gray-600 dark:text-slate-300">
              {createdChild.nickname} is ready to use MyPiggy.
            </p>

            <div className="mt-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-5">
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                {createdChild.first_name}’s Permanent Login ID
              </p>

              <div className="mt-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-white dark:bg-slate-900 px-4 py-3">
                <p className="break-all font-mono text-xl sm:text-2xl font-bold tracking-wide text-gray-900 dark:text-slate-100">
                  {createdChild.user_id}
                </p>
              </div>

              <Button
                type="button"
                variant="secondary"
                className="mt-3 w-full"
                onClick={handleCopyLoginId}
              >
                {copied
                  ? 'Copied!'
                  : 'Copy Login ID'}
              </Button>

              <p className="mt-3 text-sm text-blue-800 dark:text-blue-200">
                Your child signs in with this Login ID and the password you created.
              </p>
            </div>

            <div className="mt-5 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
              Find this Login ID again under Manage Child. Parents and Kids app download cards are always available in Settings.
            </div>

            {error && (
              <div
                className="mt-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-4 py-3 text-sm text-red-700 dark:text-red-300"
                role="alert"
              >
                {error}
              </div>
            )}

            <div className="mt-4"><AppDownloadCard audience="kids" /></div>

            <Button
              type="button"
              size="lg"
              className="mt-4 w-full"
              onClick={startDashboardTour}
              disabled={saving}
            >
              {saving
                ? 'Starting Tour...'
                : 'Start Dashboard Tour'}
            </Button>
          </Card>
        )}

      </div>
    </AppLayout>
  );
}

